import React from "react";
import axios from "axios";

import { LocalTokenStorage } from "@egis-auth/client";
import AuthenticationService, {
  LocalStorageKey,
} from "./AuthenticationService";
import { clearMap } from "../../../lib/carto/main";
import { setInitialized } from "../../ImageReview/LocationControl";
import { LegacyAuthContext, LegacyAuthState } from "./legacyAuthContext";
import { StadePdaToken } from "./legacyAuthModel";

type AuthProviderProps = React.PropsWithChildren<{
  /**
   * Delay between two token validity checks in ms.
   * Note that [4 ms is the minimum for HTML5](https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout#Reasons_for_delays_longer_than_specified).
   * (default to `4` ms)
   */
  checkTokenValidityDelay?: number;
}>;

function LegacyAuthProvider(props: AuthProviderProps) {
  const { children, checkTokenValidityDelay = 4 } = props;

  // Hack to force an authState update.
  const [update, updateState] = React.useState<{}>();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const authApiEndpoint = process.env.REACT_APP_AUTH_API_ENDPOINT;
  if (authApiEndpoint === undefined)
    throw new Error("missing key 'REACT_APP_AUTH_API_ENDPOINT' in dotenv");

  const authenticationService: AuthenticationService<StadePdaToken> =
    React.useMemo(() => {
      return new AuthenticationService<StadePdaToken>({
        endpoint: authApiEndpoint,
        storage: new LocalTokenStorage<StadePdaToken>({
          key: LocalStorageKey.Token,
        }),
      });
    }, [authApiEndpoint]);
  const [token, setToken] = React.useState<string>();
  const [loggedIn, setLoggedIn] = React.useState(
    authenticationService.isLoggedIn()
  );
  const [cityId, setCityId] = React.useState<string>();

  // Preset some things when the user return to the app without logout.
  React.useEffect(() => {
    try {
      const token = authenticationService.getRawToken();
      const loggedIn = authenticationService.isLoggedIn();
      const cityId = authenticationService.getToken().codeVille;

      setToken(token);
      setLoggedIn(loggedIn);
      setCityId(cityId);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
    }
  }, [authenticationService]);

  /**
   * Wrapper around `AuthenticationService<StadePdaToken>["cardLogin"]`.
   *
   * @param matricule - The matricule of the agent.
   * @param pinCode - The pin code of the agent.
   * @param deviceId - The NFC device ID.
   * @param cardUid - The NFC card ID.
   * @param keepSessionOpen - The flag to keep the session open or not.
   */
  const login: LegacyAuthState["login"] = React.useCallback(
    (
      matricule: string,
      pinCode: string,
      deviceId: string,
      cardUid: string,
      keepSessionOpen: "0" | "1"
    ) => {
      return new Promise<string>((resolve, reject) => {
        authenticationService
          .cardLogin(matricule, pinCode, deviceId, cardUid)
          .then((ujwt) => {
            authenticationService.setUserCredentials(
              matricule,
              pinCode,
              keepSessionOpen
            );
            try {
              const token = authenticationService.getRawToken();
              const loggedIn = authenticationService.isLoggedIn();
              const cityId = authenticationService.getToken().codeVille;

              setToken(token);
              setLoggedIn(loggedIn);
              setCityId(cityId);
            } catch (error) {
              throw error;
            }
            resolve(ujwt);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    [authenticationService]
  );

  /**
   * Wrapper around `AuthenticationService<StadePdaToken>["logout"]`.
   *
   * @param mode - The logout mode, `"soft"` means keep the user credentials,
   * `"hard"` means remove the user credentials.
   */
  const logout: LegacyAuthState["logout"] = React.useCallback(
    (mode: "soft" | "hard" = "soft") => {
      if (mode === "hard") {
        authenticationService.removeUserCredentials();
        clearMap();
        setInitialized(false);
        forceUpdate(); // Just in case we soft logout before.
      }
      authenticationService.logout();
      setToken(undefined);
      setLoggedIn(false);
      setCityId(undefined);
    },
    [authenticationService, forceUpdate]
  );

  /**
   * Check the token validity, if invalid -> logout in a choosen mode.
   *
   * @param mode - The logout mode, `"soft"` means keep the user credentials,
   * `"hard"` means remove the user credentials.
   */
  const checkTokenValidity = React.useCallback(
    (mode: "soft" | "hard") => {
      try {
        if (authenticationService.isTokenExpired()) {
          // The token is expired.
          logout(mode);
        }
      } catch (error) {
        // The token is invalid.
        if (process.env.NODE_ENV !== "production") console.error(error);
        logout(mode);
      }
    },
    [authenticationService, logout]
  );

  // Here we will regularly check the token validity, if the token is expired
  // or invalid or the user is logged out, we will silently remove it from
  // the local storage and set the loggedIn flag to `false` to notify
  // the component(s) that subscribed to it that the user is logged out.
  React.useEffect(() => {
    if (!loggedIn) return;

    const { keepSessionOpen } = authenticationService.getUserCredentials();
    const mode = keepSessionOpen === "1" ? "soft" : "hard";

    checkTokenValidity(mode);
    const intervalId = setInterval(
      checkTokenValidity,
      checkTokenValidityDelay,
      mode
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [
    authenticationService,
    checkTokenValidity,
    checkTokenValidityDelay,
    loggedIn,
  ]);

  React.useEffect(() => {
    const { keepSessionOpen } = authenticationService.getUserCredentials();
    const mode = keepSessionOpen === "1" ? "soft" : "hard";

    const unauthorizedInterceptor = axios.interceptors.response.use(
      function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        return response;
      },
      function (error) {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        if (error.response.status === 401) {
          checkTokenValidity(mode);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(unauthorizedInterceptor);
    };
  }, [authenticationService, checkTokenValidity]);

  const legacyAuthState: LegacyAuthState = React.useMemo(() => {
    return {
      authenticationService,
      cityId,
      loggedIn,
      login,
      logout,
      token,
      update,
    };
  }, [authenticationService, cityId, loggedIn, login, logout, token, update]);

  return (
    <LegacyAuthContext.Provider value={legacyAuthState}>
      {children}
    </LegacyAuthContext.Provider>
  );
}

export default LegacyAuthProvider;
